import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  CheckCircle2,
  Download,
  Calendar,
  Users,
  Ticket,
  ChevronLeft,
  FileText,
  ArrowRight,
  Sparkles,
  Check,
  Mail,
  Smartphone,
  QrCode,
} from "lucide-react";
import { toast } from "react-toastify";
import Footer from "../../components/landing/Footer";

export default function EventRegistrationSuccess() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [bookingId, setBookingId] = useState(null);
  const [eventData, setEventData] = useState(null);
  const [ticketSelection, setTicketSelection] = useState(null);
  const [guests, setGuests] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [resendingInvite, setResendingInvite] = useState(false);

  useEffect(() => {
    // Load success data from sessionStorage
    const savedSuccessData = sessionStorage.getItem(`eventBookingSuccess_${slug}`);
    if (!savedSuccessData) {
      toast.error("Booking information not found");
      navigate(`/event-details/${slug}`);
      return;
    }

    try {
      const parsed = JSON.parse(savedSuccessData);
      if (!parsed.bookingId || !parsed.eventData) {
        toast.error("Invalid booking data");
        navigate(`/event-details/${slug}`);
        return;
      }

      setBookingId(parsed.bookingId);
      setEventData(parsed.eventData);
      setTicketSelection(parsed.ticketSelection);
      setGuests(parsed.guests || []);
    } catch (error) {
      console.error("Error loading success data:", error);
      toast.error("Failed to load booking data");
      navigate(`/event-details/${slug}`);
    }

    // Cleanup: Remove success data when component unmounts (user navigates away)
    return () => {
      sessionStorage.removeItem(`eventBookingSuccess_${slug}`);
    };
  }, [slug, navigate]);

  const handleDownloadReceipt = async () => {
    setDownloading(true);
    try {
      // Simulated download - replace with actual API call
      await new Promise((resolve) => setTimeout(resolve, 1500));
      toast.success("Receipt downloaded successfully!");
    } catch (error) {
      toast.error("Failed to download receipt");
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadInvitation = async () => {
    setDownloading(true);
    try {
      // Backend: GET/POST to e.g. /user/events/booking/{bookingId}/invitation/download
      await new Promise((resolve) => setTimeout(resolve, 1200));
      toast.info("Invitation download will be available once backend is connected.");
    } catch (error) {
      toast.error("Failed to download invitation");
    } finally {
      setDownloading(false);
    }
  };

  const handleResendInvitation = async (channel) => {
    setResendingInvite(true);
    try {
      // Backend: POST e.g. /user/events/booking/{bookingId}/invitation/resend { channel: "email" | "sms" }
      await new Promise((resolve) => setTimeout(resolve, 1200));
      toast.info(`${channel === "email" ? "Email" : "SMS"} invitation will be resent once backend is connected.`);
    } catch (error) {
      toast.error("Failed to resend invitation");
    } finally {
      setResendingInvite(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Date TBA";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "Date TBA";
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    try {
      const [hours, minutes] = timeString.split(":");
      const hour12 = parseInt(hours) % 12 || 12;
      const ampm = parseInt(hours) >= 12 ? "PM" : "AM";
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  if (!bookingId || !eventData) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50">
      <div className="px-4 py-8 mt-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl">
          <motion.button
            onClick={() => navigate("/events")}
            whileHover={{ x: -4 }}
            className="flex gap-2 items-center px-4 py-2 mb-8 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-200 shadow-sm transition-all hover:bg-gray-50 hover:shadow-md"
          >
            <ChevronLeft size={18} />
            Back to Events
          </motion.button>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Success Header */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center mb-8"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 150 }}
                  className="flex justify-center mb-6"
                >
                  <div className="relative">
                    <div className="flex justify-center items-center w-32 h-32 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full border-4 border-green-300 shadow-2xl">
                      <CheckCircle2 size={64} className="text-white drop-shadow-lg" />
                    </div>
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: [1, 1.3, 1] }}
                      transition={{ delay: 0.5, duration: 2, repeat: Infinity }}
                      className="absolute inset-0 bg-green-400 rounded-full opacity-20"
                    />
                  </div>
                </motion.div>

                <motion.h1
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="mb-3 text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-700 sm:text-5xl"
                >
                  Booking Confirmed!
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="mb-6 text-lg text-gray-600"
                >
                  Your event tickets have been successfully booked
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 }}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-100 to-teal-100 rounded-full border-2 border-emerald-300 shadow-lg"
                >
                  <Ticket size={20} className="text-emerald-700" />
                  <span className="text-sm font-semibold text-gray-700">Booking ID:</span>
                  <span className="font-mono text-lg font-bold text-emerald-900">{bookingId}</span>
                </motion.div>
              </motion.div>

              {/* Event Details Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="p-6 mb-6 bg-white rounded-2xl border-2 border-gray-200 shadow-lg"
              >
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Event Details</h2>
                
                <div className="space-y-4">
                  <div className="flex gap-4 items-start">
                    <div className="flex-shrink-0 p-3 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-xl">
                      <Calendar size={24} className="text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Event</p>
                      <p className="text-lg font-bold text-gray-900">{eventData.event_title}</p>
                      <p className="text-sm text-gray-600 mt-1">
                        {(() => {
                        const start = eventData.event_start_date || eventData.event_date;
                        const end = eventData.event_end_date || eventData.event_date;
                        const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");
                        const dateStr = start && end && start !== end ? `${fmt(start)} – ${fmt(end)}` : fmt(start);
                        const startTime = eventData.event_start_time || eventData.event_time;
                        const endTime = eventData.event_end_time || eventData.event_time;
                        const timeStr = startTime && endTime && startTime !== endTime
                          ? `${formatTime(startTime)} – ${formatTime(endTime)}`
                          : formatTime(startTime || eventData.event_time);
                        return `${dateStr} • ${timeStr}`;
                      })()}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4 items-start">
                    <div className="flex-shrink-0 p-3 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-xl">
                      <Ticket size={24} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Tickets</p>
                      <p className="text-lg font-bold text-gray-900">{ticketSelection.ticketType.name}</p>
                      <p className="text-sm text-gray-600 mt-1">
                        {ticketSelection.quantity} {ticketSelection.quantity > 1 ? "Tickets" : "Ticket"} • {ticketSelection.totalAmount === 0 ? "FREE" : `GH₵ ${ticketSelection.totalAmount}`}
                      </p>
                    </div>
                  </div>

                  {guests.length > 0 && (
                    <div className="flex gap-4 items-start">
                      <div className="flex-shrink-0 p-3 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-xl">
                        <Users size={24} className="text-teal-600" />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Guests</p>
                        <div className="space-y-2">
                          {guests.map((guest, index) => (
                            <div key={index} className="flex gap-2 items-center text-sm">
                              <Check size={16} className="text-green-600" />
                              <span className="font-medium text-gray-900">{guest.name}</span>
                              <span className="text-gray-500">•</span>
                              <span className="text-gray-600">{guest.email}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Action Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="grid gap-4 md:grid-cols-2"
              >
                <motion.button
                  onClick={handleDownloadReceipt}
                  disabled={downloading}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex gap-2 justify-center items-center px-6 py-4 text-base font-bold text-emerald-700 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border-2 border-emerald-300 shadow-lg transition-all hover:from-emerald-100 hover:to-teal-100 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {downloading ? (
                    <>
                      <div className="w-5 h-5 rounded-full border-2 border-indigo-600 animate-spin border-t-transparent" />
                      <span>Downloading...</span>
                    </>
                  ) : (
                    <>
                      <Download size={20} />
                      Download Receipt
                    </>
                  )}
                </motion.button>

                <motion.button
                  onClick={() => navigate("/my-event-registrations")}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex gap-2 justify-center items-center px-6 py-4 text-base font-bold text-white bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl shadow-xl transition-all hover:from-emerald-600 hover:to-teal-700 hover:shadow-2xl"
                >
                  <FileText size={20} />
                  My Event Bookings
                  <ArrowRight size={18} />
                </motion.button>
              </motion.div>

              {/* Invitation Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.75 }}
                className="mt-8 p-6 bg-white rounded-2xl border-2 border-teal-200 shadow-lg"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <QrCode size={24} className="text-teal-600" />
                  Your invitation
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  After successful payment, we send <strong>individual invitations</strong> via <strong>SMS</strong> or <strong>Email</strong> (with a download link) to each attendee.
                </p>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Each invitation includes:</p>
                <ul className="grid grid-cols-1 gap-1.5 mb-5 text-sm text-gray-700 sm:grid-cols-2">
                  <li className="flex items-center gap-2"><Check size={14} className="text-teal-500 flex-shrink-0" /> Unique QR Code / Ticket ID</li>
                  <li className="flex items-center gap-2"><Check size={14} className="text-teal-500 flex-shrink-0" /> Event name & organizer</li>
                  <li className="flex items-center gap-2"><Check size={14} className="text-teal-500 flex-shrink-0" /> Start & end date & time</li>
                  <li className="flex items-center gap-2"><Check size={14} className="text-teal-500 flex-shrink-0" /> Venue</li>
                  <li className="flex items-center gap-2"><Check size={14} className="text-teal-500 flex-shrink-0" /> Attendee name, organization</li>
                  <li className="flex items-center gap-2"><Check size={14} className="text-teal-500 flex-shrink-0" /> Email, phone, address</li>
                  <li className="flex items-center gap-2"><Check size={14} className="text-teal-500 flex-shrink-0" /> Country & city</li>
                </ul>
                <div className="flex flex-wrap gap-3">
                  <motion.button
                    onClick={handleDownloadInvitation}
                    disabled={downloading}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-teal-700 bg-teal-50 rounded-xl border border-teal-200 hover:bg-teal-100 disabled:opacity-50"
                  >
                    <Download size={18} />
                    Download invitation
                  </motion.button>
                  <motion.button
                    onClick={() => handleResendInvitation("email")}
                    disabled={resendingInvite}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl border border-gray-200 hover:bg-gray-200 disabled:opacity-50"
                  >
                    <Mail size={18} />
                    Resend to email
                  </motion.button>
                  <motion.button
                    onClick={() => handleResendInvitation("sms")}
                    disabled={resendingInvite}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl border border-gray-200 hover:bg-gray-200 disabled:opacity-50"
                  >
                    <Smartphone size={18} />
                    Resend via SMS
                  </motion.button>
                </div>
              </motion.div>

              {/* What's Next Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="mt-8 p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border-2 border-blue-200"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Sparkles size={24} className="text-emerald-600" />
                  What's Next?
                </h3>
                <div className="space-y-3">
                  <div className="flex gap-3 items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-white">1</span>
                    </div>
                    <p className="text-sm text-gray-700">
                      <strong>Check your email or SMS</strong> for your invitation with download link
                    </p>
                  </div>
                  <div className="flex gap-3 items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-white">2</span>
                    </div>
                    <p className="text-sm text-gray-700">
                      <strong>Download your receipt</strong> for your records and entry
                    </p>
                  </div>
                  <div className="flex gap-3 items-start">
                    <div className="flex-shrink-0 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-white">3</span>
                    </div>
                    <p className="text-sm text-gray-700">
                      <strong>Mark your calendar</strong> and we'll see you at the event!
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="sticky top-24 p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border-2 border-green-200 shadow-lg"
              >
                <div className="flex gap-3 items-center mb-6">
                  <div className="flex justify-center items-center w-12 h-12 bg-green-500 rounded-xl shadow-md">
                    <CheckCircle2 size={24} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Confirmed</h3>
                </div>

                <div className="space-y-4">
                  <div className="p-4 bg-white rounded-xl border border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Check size={16} className="text-green-600" />
                      <p className="text-sm font-semibold text-green-900">Payment Confirmed</p>
                    </div>
                    <p className="text-xs text-gray-600">Your payment has been successfully processed</p>
                  </div>

                  <div className="p-4 bg-white rounded-xl border border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Check size={16} className="text-green-600" />
                      <p className="text-sm font-semibold text-green-900">Tickets Issued</p>
                    </div>
                    <p className="text-xs text-gray-600">Your tickets are ready for the event</p>
                  </div>

                  <div className="p-4 bg-white rounded-xl border border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Check size={16} className="text-green-600" />
                      <p className="text-sm font-semibold text-green-900">Invitation sent</p>
                    </div>
                    <p className="text-xs text-gray-600">Individual invitations (SMS or Email with download link) will be sent to each attendee</p>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-white rounded-xl border border-emerald-200">
                  <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-2">
                    Need Help?
                  </p>
                  <p className="text-sm text-gray-700 mb-3">
                    Contact our support team if you have any questions about your booking.
                  </p>
                  <button className="w-full px-4 py-2 text-sm font-bold text-emerald-700 bg-emerald-50 rounded-lg border border-emerald-200 transition-all hover:bg-emerald-100">
                    Contact Support
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

